import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Tooltip,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import CategoryIcon from '@mui/icons-material/Category';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BookIcon from '@mui/icons-material/Book';
import BalanceIcon from '@mui/icons-material/Balance';
import BuildIcon from '@mui/icons-material/Build';
import PoolIcon from '@mui/icons-material/Pool';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BackupIcon from '@mui/icons-material/Backup';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../../store/AuthContext';

const DRAWER_WIDTH = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const mainNavItems: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Units', path: '/units', icon: <ApartmentIcon /> },
  { label: 'Owners', path: '/owners', icon: <PeopleIcon /> },
];

const billingNavItems: NavItem[] = [
  { label: 'Invoices', path: '/billing/invoices', icon: <ReceiptIcon /> },
  { label: 'Payments', path: '/billing/payments', icon: <PaymentIcon /> },
  { label: 'Fee Types', path: '/billing/fee-types', icon: <CategoryIcon /> },
];

const accountingNavItems: NavItem[] = [
  { label: 'Chart of Accounts', path: '/accounting/chart-of-accounts', icon: <AccountBalanceIcon /> },
  { label: 'Journal Entries', path: '/accounting/journal-entries', icon: <BookIcon /> },
  { label: 'Trial Balance', path: '/accounting/trial-balance', icon: <BalanceIcon /> },
  { label: 'Aging Report', path: '/accounting/aging', icon: <AssessmentIcon /> },
  { label: 'P&L Statement', path: '/accounting/profit-loss', icon: <TrendingUpIcon /> },
];

const otherNavItems: NavItem[] = [
  { label: 'Maintenance', path: '/maintenance', icon: <BuildIcon /> },
  { label: 'Amenities', path: '/amenities', icon: <PoolIcon /> },
  { label: 'Amenity Bookings', path: '/amenities/bookings', icon: <CalendarMonthIcon /> },
];

const systemNavItems: NavItem[] = [
  { label: 'Backup & Restore', path: '/system/backups', icon: <BackupIcon /> },
  { label: 'Fund Types', path: '/system/fund-types', icon: <AccountTreeIcon /> },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  const renderNavItems = (items: NavItem[]) => (
    items.map((item) => (
      <ListItem key={item.path} disablePadding>
        <ListItemButton
          selected={isActive(item.path)}
          onClick={() => handleNavigation(item.path)}
          sx={{
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
            },
            '&:hover': {
              backgroundColor: 'action.hover',
            },
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} />
        </ListItemButton>
      </ListItem>
    ))
  );

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Condo Manager
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>
          MAIN
        </Typography>
        {renderNavItems(mainNavItems)}

        <Typography variant="caption" sx={{ px: 2, py: 1, mt: 1, display: 'block', color: 'text.secondary' }}>
          BILLING
        </Typography>
        {renderNavItems(billingNavItems)}

        <Typography variant="caption" sx={{ px: 2, py: 1, mt: 1, display: 'block', color: 'text.secondary' }}>
          ACCOUNTING
        </Typography>
        {renderNavItems(accountingNavItems)}

        <Typography variant="caption" sx={{ px: 2, py: 1, mt: 1, display: 'block', color: 'text.secondary' }}>
          OTHER
        </Typography>
        {renderNavItems(otherNavItems)}

        <Typography variant="caption" sx={{ px: 2, py: 1, mt: 1, display: 'block', color: 'text.secondary' }}>
          SYSTEM
        </Typography>
        {renderNavItems(systemNavItems)}
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" noWrap>
          {user?.fullName}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {user?.email}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Condo Management System
          </Typography>
          <Tooltip title="Logout">
            <Button color="inherit" onClick={logout} startIcon={<LogoutIcon />}>
              Logout
            </Button>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          ml: { md: `${DRAWER_WIDTH}px` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;
